import React, { useEffect, useState } from "react";
import GovernanceCard from "./GovernanceCard";
import { evaluateProposalResult, fetchProposals } from "../config/snapshot";
import { Spinner } from "react-bootstrap";
import { ENV } from "../config/config";
import { isVoteFinalized } from "../config/web3";

const GovernanceContent = ({ type, onRegisterRefresh }) => {
  const [proposalList, setProposalList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    hasMore: false
  });

  const quorum = ENV.quorum;
  const vetoThreshold = ENV.vetoThreshold;

  useEffect(() => {
    fetchProposalsFn(1);
  }, [type]);

  // Register the refresh function with the parent component
  useEffect(() => {
    if (onRegisterRefresh) {
      onRegisterRefresh(type, refreshCurrentPage);
    }
  }, [type, onRegisterRefresh]);

  const fetchProposalsFn = async (page = 1) => {
    try {
      setLoading(true);
      const res = await fetchProposals(page, pagination.limit);
      
      let filteredProposals = [];
      
      if (type === 'voting') {
        filteredProposals = res.proposals;
      } else if (type === 'passed') {
        filteredProposals = res.proposals.filter(proposal => {
          const result = evaluateProposalResult(proposal, quorum, vetoThreshold);
          return result === "Approved";
        });
      } else if (type === 'rejected') {
        filteredProposals = res.proposals.filter(proposal => {
          const result = evaluateProposalResult(proposal, quorum, vetoThreshold);
          return result === "Rejected" || result === "Rejected (Quorum not met)";
        });
      } else if (type === 'finalize') {
        const finalizePromises = res.proposals.map(async (proposal) => {
          const result = evaluateProposalResult(proposal, quorum, vetoThreshold);
          if (result === "Rejected" || result === "Approved" || result === "Rejected (Quorum not met)") {
            const isFinalized = await isVoteFinalized(proposal.id);
            return !isFinalized ? proposal : null;
          }
          return null;
        });
        
        const finalizeResults = await Promise.all(finalizePromises);
        filteredProposals = finalizeResults.filter(proposal => proposal !== null);
      }
      
      setProposalList(filteredProposals);
      setCurrentPage(page);
      setPagination(res.pagination);
    } catch (err) {
      console.error("Failed to fetch proposals:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasMore) {
      fetchProposalsFn(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      fetchProposalsFn(currentPage - 1);
    }
  };

  const handlePageChange = (page) => {
    fetchProposalsFn(page);
  };

  // Generate page numbers for pagination
  const generatePageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    // Simple pagination - show current page and a few around it
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = startPage + maxVisible - 1;
    
    // Adjust if we don't have enough pages after current
    if (endPage > currentPage + 2 && !pagination.hasMore) {
      endPage = currentPage;
    }
    
    for (let i = startPage; i <= endPage; i++) {
      if (i === currentPage || (i < currentPage) || (i === currentPage + 1 && pagination.hasMore)) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const refreshCurrentPage = () => {
    fetchProposalsFn(currentPage);
  };

  return (
    <div className="card-component p-3">
      {type === 'finalize' ? (
        <p className="text-danger w-100 text-center">
          Only Admin can finalize the vote results.
          Admin Address:
          <span className="text-primary"> {ENV.adminAddress}</span>
        </p>
      ) : ''}
      
      <div className="row m-0">
        {loading ? (
          <div className="text-center w-100 py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden"><Spinner /></span>
            </div>
            <p className="mt-2">Fetching proposals...</p>
          </div>
        ) : proposalList.length ? (
          <>
            {proposalList.map((data, idx) => (
              <GovernanceCard 
                key={data.id || idx} 
                proposal={data} 
                type={type} 
                fetchProposalsFn={refreshCurrentPage} 
              />
            ))}
            
            {/* Modern Pagination Controls */}
            <div className="col-12 mt-5">
              <style jsx>{`
                .modern-pagination {
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  gap: 8px;
                  margin: 2rem 0;
                }

                .pagination-btn {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  min-width: 40px;
                  height: 40px;
                  padding: 0 12px;
                  border: none;
                  border-radius: 12px;
                  font-size: 14px;
                  font-weight: 500;
                  cursor: pointer;
                  transition: all 0.2s ease;
                  background: #f8f9fa;
                  color: #495057;
                  position: relative;
                  overflow: hidden;
                }

                .pagination-btn:hover:not(:disabled) {
                  background: #e9ecef;
                  transform: translateY(-1px);
                  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                }

                .pagination-btn:active:not(:disabled) {
                  transform: translateY(0);
                }

                .pagination-btn:disabled {
                  opacity: 0.4;
                  cursor: not-allowed;
                  transform: none;
                }

                .pagination-btn.active {
                  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                  color: white;
                  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
                }

                .pagination-btn.active:hover {
                  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
                  transform: translateY(-1px);
                  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
                }

                .pagination-nav {
                  display: flex;
                  align-items: center;
                  gap: 4px;
                  font-size: 13px;
                  font-weight: 600;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                }

                .pagination-nav svg {
                  width: 16px;
                  height: 16px;
                  transition: transform 0.2s ease;
                }

                .pagination-btn:hover .pagination-nav svg {
                  transform: translateX(2px);
                }

                .pagination-btn:hover .pagination-nav.prev svg {
                  transform: translateX(-2px);
                }

                .pagination-ellipsis {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 40px;
                  height: 40px;
                  color: #6c757d;
                  font-weight: 600;
                }

                .pagination-info {
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  gap: 8px;
                  margin-top: 1rem;
                  padding: 12px 20px;
                  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                  border-radius: 12px;
                  font-size: 13px;
                  color: #495057;
                  font-weight: 500;
                }

                .pagination-info .dot {
                  width: 4px;
                  height: 4px;
                  border-radius: 50%;
                  background: #6c757d;
                }

                .pagination-info .highlight {
                  color: #667eea;
                  font-weight: 600;
                }

                @media (max-width: 576px) {
                  .modern-pagination {
                    gap: 4px;
                  }
                  
                  .pagination-btn {
                    min-width: 36px;
                    height: 36px;
                    padding: 0 8px;
                    font-size: 13px;
                  }
                  
                  .pagination-nav {
                    font-size: 11px;
                  }
                  
                  .pagination-info {
                    font-size: 12px;
                    padding: 8px 16px;
                  }
                }
              `}</style>

              <div className="modern-pagination">
                {/* Previous Button */}
                <button 
                  className="pagination-btn"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  <div className="pagination-nav prev">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    <span>Prev</span>
                  </div>
                </button>
                
                {/* Page Numbers */}
                {generatePageNumbers().map(pageNum => (
                  <button 
                    key={pageNum}
                    className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                ))}
                
                {/* Ellipsis */}
                {pagination.hasMore && (
                  <div className="pagination-ellipsis">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="1"/>
                      <circle cx="19" cy="12" r="1"/>
                      <circle cx="5" cy="12" r="1"/>
                    </svg>
                  </div>
                )}
                
                {/* Next Button */}
                <button 
                  className="pagination-btn"
                  onClick={handleNextPage}
                  disabled={!pagination.hasMore}
                >
                  <div className="pagination-nav">
                    <span>Next</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </div>
                </button>
              </div>
              
              {/* Modern Pagination Info */}
              <div className="pagination-info">
                <span>Page</span>
                <span className="highlight">{currentPage}</span>
                <div className="dot"></div>
                <span><span className="highlight">{proposalList.length}</span> proposals shown</span>
                {pagination.hasMore && (
                  <>
                    <div className="dot"></div>
                    <span className="highlight">More available</span>
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          <p className="text-danger w-100 text-center">
            Sorry, we don't have any {type === 'finalize' ? "to be finalized" : type} proposals right now!
          </p>
        )}
      </div>
    </div>
  );
};

export default GovernanceContent;